import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getYoutuberAdmin,
  listVideosAdmin,
  updateVideoAdmin,
  geolocateVideoOne,
  importLatestVideos,
  refreshChannelMeta,
  refreshYoutuberViews,
  processVideoContent,
  retranscribeVideo,
  youtuberVideoCounts,
  updateYoutuber,
  importTranscriptsBulk,
  getSettings,
} from "@/lib/admin.functions";
import { formatCompact, formatDate, formatDuration, cleanTitle } from "@/lib/format";
import { BATCH_OPTIONS, DEFAULT_BATCH_SIZE, normalizeBatchSize } from "@/lib/batch-options";
import { useTranslation } from "react-i18next";
import { MapView, type MapPin as MapPinT } from "@/components/map-view";
import { useMemo, useState, useRef, useEffect } from "react";
import { toast } from "sonner";
import {
  MapPin,
  EyeOff,
  Pencil,
  Ban,
  RotateCcw,
  Eye,
  Download,
  RefreshCw,
  ArrowLeft,
  ExternalLink,
  Youtube,
  Sparkles,
  FileText,
  Loader2,
  ChevronDown,
  Zap,
  X,
  CheckCircle2,
  AlertCircle,
} from "lucide-react";


export const Route = createFileRoute("/_authenticated/admin/youtubers/$id")({
  staticData: { sitemap: false },
  component: AdminYoutuberDetail,
});

const PAGE_SIZE_OPTIONS = [25, 50, 100, 200, 500] as const;

type Progress = { current: number; total: number; indeterminate?: boolean } | null;

// Rough public list prices (USD per 1M tokens). Used for approximate cost display.
const MODEL_PRICING: Record<string, { in: number; out: number }> = {
  "gemini-2.5-flash": { in: 0.3, out: 2.5 },
  "gemini-2.5-flash-lite": { in: 0.1, out: 0.4 },
  "gemini-2.5-pro": { in: 1.25, out: 10 },
  "gemini-2.0-flash": { in: 0.1, out: 0.4 },
  "gemini-2.0-flash-lite": { in: 0.075, out: 0.3 },
  "gemini-1.5-pro": { in: 1.25, out: 5 },
  "gemini-1.5-flash": { in: 0.075, out: 0.3 },
  "gpt-4o-mini": { in: 0.15, out: 0.6 },
  "gpt-4o": { in: 2.5, out: 10 },
  "gpt-4.1-mini": { in: 0.4, out: 1.6 },
  "gpt-5-mini": { in: 0.25, out: 2 },
  "gpt-5": { in: 1.25, out: 10 },
  "gpt-5-nano": { in: 0.05, out: 0.4 },
};

function normalizeModel(model: string): string {
  return (model || "")
    .toLowerCase()
    .replace(/^[a-z0-9_-]+\//, "") // strip vendor prefix (google/, openai/)
    .replace(/-(latest|exp)(-\d+)?$/g, "") // strip trailing tags
    .replace(/-preview.*$/g, "")
    .trim();
}

function estimateCostUsd(model: string, prompt: number, completion: number): number {
  const key = normalizeModel(model);
  let p = MODEL_PRICING[key];
  if (!p) {
    // fuzzy: find longest key that is a prefix of the normalized model
    const match = Object.keys(MODEL_PRICING)
      .filter((k) => key.startsWith(k))
      .sort((a, b) => b.length - a.length)[0];
    if (match) p = MODEL_PRICING[match];
  }
  if (!p) return 0;
  return (prompt * p.in + completion * p.out) / 1_000_000;
}

function fmtCost(n: number): string {
  if (n <= 0) return "$0";
  if (n < 0.0001) return `$${n.toFixed(6)}`;
  if (n < 0.01) return `$${n.toFixed(4)}`;
  if (n < 1) return `$${n.toFixed(3)}`;
  return `$${n.toFixed(2)}`;
}

function ProgressButton({
  onClick,
  disabled,
  progress,
  className = "",
  variant = "outline",
  icon,
  label,
  runningLabel,
  title,
}: {
  onClick: () => void;
  disabled?: boolean;
  progress: Progress;
  className?: string;
  variant?: "primary" | "outline";
  icon: React.ReactNode;
  label: string;
  runningLabel?: string;
  title?: string;
}) {
  const running = progress !== null;
  const pct = progress && !progress.indeterminate && progress.total > 0
    ? Math.min(100, (progress.current / progress.total) * 100)
    : 0;
  const base =
    variant === "primary"
      ? "bg-ink text-paper hover:bg-amber"
      : "ring-hairline hover:bg-secondary";
  return (
    <button
      onClick={onClick}
      disabled={disabled || running}
      title={title}
      className={`relative overflow-hidden flex items-center gap-2 px-4 py-2 text-sm transition-colors disabled:opacity-70 ${base} ${className}`}
    >

      {running && (
        <>
          {progress?.indeterminate ? (
            <span
              aria-hidden
              className="absolute inset-y-0 left-0 w-1/3 bg-amber/25 animate-[progress-slide_1.2s_ease-in-out_infinite]"
            />
          ) : (
            <span
              aria-hidden
              className="absolute inset-y-0 left-0 bg-amber/30 transition-all duration-200"
              style={{ width: `${pct}%` }}
            />
          )}
        </>
      )}
      <span className="relative flex items-center gap-2">
        {icon}
        {running
          ? progress?.indeterminate
            ? (runningLabel ?? `${label}…`)
            : `${runningLabel ?? label} ${progress?.current}/${progress?.total}`
          : label}
      </span>
    </button>
  );
}

function AdminYoutuberDetail() {
  const { id } = Route.useParams();
  const qc = useQueryClient();

  const getYtFn = useServerFn(getYoutuberAdmin);
  const listFn = useServerFn(listVideosAdmin);

  const { data: youtuber } = useQuery({
    queryKey: ["admin", "youtuber", id],
    queryFn: () => getYtFn({ data: { id } }),
  });

  const [filters, setFilters] = useState<{
    onlyMissingGeo: boolean;
    onlyMissingTranscript: boolean;
    onlyMissingSummary: boolean;
    onlyMissingCleanDescription: boolean;
    includeHidden: boolean;
    includeDeleted: boolean;
    search: string;
  }>({
    onlyMissingGeo: false,
    onlyMissingTranscript: false,
    onlyMissingSummary: false,
    onlyMissingCleanDescription: false,
    includeHidden: false,
    includeDeleted: false,
    search: "",
  });

  const { data: videos, isLoading } = useQuery({
    queryKey: ["admin", "videos", "by-youtuber", id, filters],
    queryFn: () => listFn({ data: { youtuberId: id, ...filters } }),
  });

  const countsFn = useServerFn(youtuberVideoCounts);
  const { data: counts } = useQuery({
    queryKey: ["admin", "video-counts", id],
    queryFn: () => countsFn({ data: { youtuberId: id } }),
  });

  const { i18n } = useTranslation();

  const updYtFn = useServerFn(updateYoutuber);
  const toggleChannelHidden = useMutation({
    mutationFn: (hidden: boolean) => updYtFn({ data: { id, patch: { hidden } } }),
    onSuccess: (_r, hidden) => {
      toast.success(hidden ? "Channel hidden from the public site" : "Channel is public again");
      qc.invalidateQueries({ queryKey: ["admin", "youtuber", id] });
      qc.invalidateQueries({ queryKey: ["admin", "youtubers"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const [editingName, setEditingName] = useState(false);
  const [nameInput, setNameInput] = useState("");
  const saveName = useMutation({
    mutationFn: (name: string) => updYtFn({ data: { id, patch: { name } } }),
    onSuccess: () => {
      toast.success("Channel name saved");
      setEditingName(false);
      qc.invalidateQueries({ queryKey: ["admin", "youtuber", id] });
      qc.invalidateQueries({ queryKey: ["admin", "youtubers"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  // Minimum publish year: videos older than this are ignored on import.
  const [minYearInput, setMinYearInput] = useState<string>("");
  useEffect(() => {
    const v = (youtuber as any)?.min_import_year;
    setMinYearInput(v == null ? "" : String(v));
  }, [youtuber]);
  const saveMinYear = useMutation({
    mutationFn: (value: number | null) => updYtFn({ data: { id, patch: { min_import_year: value } } }),
    onSuccess: () => {
      toast.success("Minimum import year saved");
      qc.invalidateQueries({ queryKey: ["admin", "youtuber", id] });
    },
    onError: (e: Error) => toast.error(e.message),
  });
  const minYearDirty =
    minYearInput.trim() !== String((youtuber as any)?.min_import_year ?? "");

  const updFn = useServerFn(updateVideoAdmin);
  const importTranscriptsFn = useServerFn(importTranscriptsBulk);
  const importTranscriptsInputRef = useRef<HTMLInputElement | null>(null);
  const upd = useMutation({
    mutationFn: (input: { id: string; patch: any }) => updFn({ data: input }),
    onMutate: async (input) => {
      // Optimistic: patch every cached admin videos list without refetching.
      await qc.cancelQueries({ queryKey: ["admin", "videos"] });
      const snapshots: Array<[readonly unknown[], any]> = [];
      qc.getQueriesData({ queryKey: ["admin", "videos"] }).forEach(([key, data]: any) => {
        if (!data) return;
        snapshots.push([key, data]);
        const patchRow = (r: any) => (r && r.id === input.id ? { ...r, ...input.patch } : r);
        if (Array.isArray(data)) {
          qc.setQueryData(key, data.map(patchRow));
        } else if (Array.isArray(data?.items)) {
          qc.setQueryData(key, { ...data, items: data.items.map(patchRow) });
        } else if (Array.isArray(data?.videos)) {
          qc.setQueryData(key, { ...data, videos: data.videos.map(patchRow) });
        }
      });
      return { snapshots };
    },
    onError: (e: Error, _vars, ctx) => {
      ctx?.snapshots?.forEach(([key, data]) => qc.setQueryData(key, data));
      toast.error(e.message);
    },
    onSettled: () => {
      // Refresh counts (hidden filter affects them); skip full videos refetch.
      qc.invalidateQueries({ queryKey: ["admin", "video-counts"] });
    },
  });

  const settingsFn = useServerFn(getSettings);
  const { data: settingsData } = useQuery({
    queryKey: ["admin", "settings"],
    queryFn: () => settingsFn(),
    staleTime: 5 * 60 * 1000,
  });
  const [batchCount, setBatchCount] = useState<number>(DEFAULT_BATCH_SIZE);
  const batchTouched = useRef(false);
  useEffect(() => {
    if (settingsData && !batchTouched.current) {
      setBatchCount(normalizeBatchSize((settingsData as any).import_batch_size));
    }
  }, [settingsData]);
  const [page, setPage] = useState<number>(1);
  const [perPage, setPerPage] = useState<number>(100);
  // Video list ordering. "newest" keeps the server order (published desc).
  const [sort, setSort] = useState<"newest" | "duration_asc" | "duration_desc">("newest");

  // Progress state per action
  const [importProgress, setImportProgress] = useState<Progress>(null);
  const [geoProgress, setGeoProgress] = useState<Progress>(null);
  const [viewsProgress, setViewsProgress] = useState<Progress>(null);
  const [contentProgress, setContentProgress] = useState<Progress>(null);
  const [metaProgress, setMetaProgress] = useState<Progress>(null);

  // Persistent job log
  type JobItem = { title: string; ok: boolean; error: string | null; detail?: string };
  type JobUsage = { prompt: number; completion: number; costUsd: number };
  type JobLog = {
    id: number;
    kind: string;
    startedAt: number;
    running: boolean;
    items: JobItem[];
    summary?: string;
    usage: JobUsage;
  };
  const [jobs, setJobs] = useState<JobLog[]>([]);
  const jobIdRef = useRef(0);
  // Clear the job log whenever the route's youtuber changes so entries don't
  // leak across pages.
  useEffect(() => {
    setJobs([]);
    jobIdRef.current = 0;
  }, [id]);
  const startJob = (kind: string): number => {
    const id = ++jobIdRef.current;
    setJobs((prev) =>
      [
        {
          id,
          kind,
          startedAt: Date.now(),
          running: true,
          items: [],
          usage: { prompt: 0, completion: 0, costUsd: 0 },
        },
        ...prev,
      ].slice(0, 5),
    );
    return id;
  };
  const appendJobItems = (id: number, items: JobItem[]) =>
    setJobs((prev) => prev.map((j) => (j.id === id ? { ...j, items: [...j.items, ...items] } : j)));
  const addJobUsage = (id: number, u: any) => {
    if (!u) return;
    let prompt = Number(u.prompt_tokens ?? 0) || 0;
    const completion = Number(u.completion_tokens ?? 0) || 0;
    const total = Number(u.total_tokens ?? 0) || 0;
    if (prompt === 0 && completion === 0 && total > 0) prompt = total;
    const cost = estimateCostUsd(u.model ?? "", prompt, completion);
    setJobs((prev) =>
      prev.map((j) =>
        j.id === id
          ? {
              ...j,
              usage: {
                prompt: j.usage.prompt + prompt,
                completion: j.usage.completion + completion,
                costUsd: j.usage.costUsd + cost,
              },
            }
          : j,
      ),
    );
  };
  const finishJob = (id: number, summary: string) =>
    setJobs((prev) => prev.map((j) => (j.id === id ? { ...j, running: false, summary } : j)));
  const dismissJob = (id: number) => setJobs((prev) => prev.filter((j) => j.id !== id));

  const importFn = useServerFn(importLatestVideos);
  const viewsFn = useServerFn(refreshYoutuberViews);
  const metaFn = useServerFn(refreshChannelMeta);
  // Row-level fns — batch actions call these one-by-one, so they behave
  // identically to clicking each row's button manually.
  const rowGeoFn = useServerFn(geolocateVideoOne);
  const rowTranscribeFn = useServerFn(retranscribeVideo);
  const rowProcessFn = useServerFn(processVideoContent);

  const runningRef = useRef(false);

  async function runImport(mode: "import" | "transcribe" | "summarize" | "all" = "all") {
    if (runningRef.current) return;
    runningRef.current = true;
    const total = batchCount;
    setImportProgress({ current: 0, total });
    let imported = 0;
    let shorts = 0;
    let existing = 0;
    let geolocated = 0;
    let failed = 0;
    const kindLabel =
      mode === "import" ? "Import (metadata only)"
      : mode === "transcribe" ? "Import + transcribe"
      : mode === "summarize" ? "Import + transcribe + summarize"
      : "Import + all";
    const jobId = startJob(kindLabel);
    try {
      // Import one video at a time so the progress bar can advance in real time
      // (auto-processing and geolocation run inside importLatestVideos and can take a while).
      for (let i = 0; i < total; i++) {
        const r: any = await importFn({ data: { youtuberId: id, count: 1, mode } });
        imported += r.imported ?? 0;
        shorts += r.skippedShorts ?? 0;
        existing += r.skippedExisting ?? 0;
        geolocated += r.geolocated ?? 0;
        failed += (r.processFailed ?? 0) + (r.geoFailed ?? 0);
        const returnedItems = Array.isArray(r.items) ? r.items : [];
        for (const item of returnedItems) {
          if (Array.isArray(item.usages)) item.usages.forEach((u: any) => addJobUsage(jobId, u));
        }
        if (returnedItems.length > 0) {
          appendJobItems(jobId, returnedItems.map((item: any) => ({
            title: item.title ?? item.id,
            ok: !!item.ok,
            error: item.ok ? null : item.error ?? "Import processing failed",
            detail: item.ok
              ? `processed · ${item.locationCount ?? 0} location${(item.locationCount ?? 0) === 1 ? "" : "s"}`
              : undefined,
          })));
        }
        setImportProgress({ current: i + 1, total });
        // Nothing more to import from the playlist — stop early.
        if ((r.imported ?? 0) === 0) break;
      }
      if (imported === 0) {
        appendJobItems(jobId, [{
          title: youtuber?.name ?? "Channel",
          ok: true,
          error: null,
          detail: `0 imported · ${shorts} shorts · ${existing} already in DB`,
        }]);
      }
      finishJob(jobId, `Imported ${imported} · geolocated ${geolocated}${failed ? ` · ${failed} failed` : ""} · ${shorts} shorts · ${existing} existing`);
      qc.invalidateQueries({ queryKey: ["admin"] });
    } catch (e: any) {
      appendJobItems(jobId, [{ title: youtuber?.name ?? "Import", ok: false, error: e?.message ?? String(e) }]);
      finishJob(jobId, `Error: ${e.message}`);
    } finally {
      setImportProgress(null);
      runningRef.current = false;
    }
  }


  async function runGeolocate(mode: "missing" | "description_only" = "missing") {
    if (runningRef.current) return;
    const list = videos ?? [];
    const targets = mode === "description_only"
      ? list.filter((v: any) => v.geolocated && v.geo_source === "description")
      : list.filter((v: any) => !v.geolocated || (v.video_locations?.length ?? 0) === 0);
    const total = Math.min(batchCount, targets.length);
    if (total === 0) {
      toast.info("Nothing to geolocate");
      return;
    }
    runningRef.current = true;
    let geolocated = 0;
    const jobId = startJob(mode === "description_only" ? "Re-geolocate oranges" : "Geolocate");
    setGeoProgress({ current: 0, total });
    try {
      for (let i = 0; i < total; i++) {
        const v = targets[i];
        try {
          const r: any = await rowGeoFn({ data: { id: v.id, replace: mode === "description_only" } });
          addJobUsage(jobId, r?.usage);
          const ok = (r.inserted ?? 0) > 0;
          if (ok) geolocated++;
          appendJobItems(jobId, [{
            title: v.title,
            ok,
            error: ok ? null : r.error ?? "No locations returned",
            detail: ok ? `${r.inserted} location${r.inserted === 1 ? "" : "s"}` : undefined,
          }]);
        } catch (e: any) {
          appendJobItems(jobId, [{ title: v.title, ok: false, error: e?.message ?? String(e) }]);
        }
        setGeoProgress({ current: i + 1, total });
      }
      finishJob(jobId, `Geolocated ${geolocated} of ${total}`);
      qc.invalidateQueries({ queryKey: ["admin"] });
    } catch (e: any) {
      appendJobItems(jobId, [{ title: youtuber?.name ?? "Geolocate", ok: false, error: e?.message ?? String(e) }]);
      finishJob(jobId, `Error: ${e.message}`);
    } finally {
      setGeoProgress(null);
      runningRef.current = false;
    }
  }

  async function runDoEverything() {
    if (runningRef.current) return;
    const list = videos ?? [];
    const needsContent = (v: any) => {
      const hasClean = !!(v.description_clean && String(v.description_clean).trim());
      const hasTr = !!(v.transcript && String(v.transcript).trim());
      const hasSum = !!(v.transcript_summary && String(v.transcript_summary).trim());
      if (!hasClean) return true;
      if (hasTr && !hasSum) return true;
      return false;
    };
    const needsGeo = (v: any) => !v.geolocated;
    const targets = list.filter((v: any) => !v.hidden && !v.deleted && (needsContent(v) || needsGeo(v)));
    const total = targets.length;
    if (total === 0) {
      toast.info("Nothing to do");
      return;
    }
    runningRef.current = true;
    let ok = 0;
    let failed = 0;
    const jobId = startJob("Do everything");
    setContentProgress({ current: 0, total });
    try {
      for (let i = 0; i < total; i++) {
        const v = targets[i];
        try {
          if (needsContent(v)) {
            const pr: any = await rowProcessFn({ data: { id: v.id, mode: "both" } });
            if (Array.isArray(pr?.usages)) pr.usages.forEach((u: any) => addJobUsage(jobId, u));
            else addJobUsage(jobId, pr?.usage);
          }
          let detail: string | undefined;
          if (needsGeo(v)) {
            const gr: any = await rowGeoFn({ data: { id: v.id, replace: false } });
            addJobUsage(jobId, gr?.usage);
            const ins = gr?.inserted ?? 0;
            detail = `${ins} location${ins === 1 ? "" : "s"}`;
          }
          ok++;
          appendJobItems(jobId, [{ title: v.title, ok: true, error: null, detail }]);
        } catch (e: any) {
          failed++;
          appendJobItems(jobId, [{ title: v.title, ok: false, error: e?.message ?? String(e) }]);
        }
        setContentProgress({ current: i + 1, total });
      }
      finishJob(jobId, `Done ${ok}${failed ? ` · ${failed} failed` : ""}`);
      qc.invalidateQueries({ queryKey: ["admin"] });
    } catch (e: any) {
      appendJobItems(jobId, [{ title: youtuber?.name ?? "Do everything", ok: false, error: e?.message ?? String(e) }]);
      finishJob(jobId, `Error: ${e.message}`);
    } finally {
      setContentProgress(null);
      runningRef.current = false;
    }
  }



  async function runRefreshViews() {
    if (runningRef.current) return;
    runningRef.current = true;
    const CHUNK = 50;
    setViewsProgress({ current: 0, total: 1, indeterminate: true });
    let updated = 0;
    let titlesChanged = 0;
    try {
      let offset = 0;
      let total = 0;
      for (;;) {
        const r: any = await viewsFn({ data: { youtuberId: id, offset, limit: CHUNK } });
        total = r.total ?? 0;
        updated += r.updated ?? 0;
        titlesChanged += r.titlesChanged ?? 0;
        const processed = r.processed ?? 0;
        offset += processed;
        setViewsProgress({ current: Math.min(offset, total || offset), total: total || offset || 1 });
        if (processed === 0 || offset >= total) break;
      }
      const titleMsg = titlesChanged ? ` · ${titlesChanged} title${titlesChanged === 1 ? "" : "s"} updated` : "";
      toast.success(`Views updated for ${updated} videos${titleMsg}`);
      qc.invalidateQueries({ queryKey: ["admin"] });
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setViewsProgress(null);
      runningRef.current = false;
    }
  }

  async function runRefreshMeta() {
    if (runningRef.current) return;
    runningRef.current = true;
    setMetaProgress({ current: 0, total: 1, indeterminate: true });
    try {
      await metaFn({ data: { id } });
      toast.success("Channel meta refreshed");
      qc.invalidateQueries({ queryKey: ["admin"] });
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setMetaProgress(null);
      runningRef.current = false;
    }
  }

  async function exportMissingTranscriptsCsv() {
    try {
      const rows: any[] = await listFn({
        data: {
          youtuberId: id,
          onlyMissingTranscript: true,
          includeHidden: false,
          limit: 5000,
        },
      });
      if (!rows || rows.length === 0) {
        toast.info("No videos missing transcript");
        return;
      }
      const esc = (v: string) => `"${String(v).replace(/"/g, '""')}"`;
      const header = ["title", "date", "url"].map(esc).join(",");
      const lines = rows.map((v: any) => {
        const title = cleanTitle(v.title ?? "");
        const date = v.published_at ? formatDate(v.published_at, i18n.language) : "";
        const url = `https://www.youtube.com/watch?v=${v.youtube_video_id}`;
        return [esc(title), esc(date), esc(url)].join(",");
      });
      const csv = "\ufeff" + [header, ...lines].join("\r\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const handle = youtuber?.handle ?? "channel";
      a.download = `${handle}-missing-transcripts.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${rows.length} rows`);
    } catch (e: any) {
      toast.error(e.message ?? String(e));
    }
  }

  async function handleImportTranscriptsFile(file: File) {
    try {
      const text = await file.text();
      let parsed: any;
      try {
        parsed = JSON.parse(text);
      } catch {
        toast.error("Invalid JSON file");
        return;
      }
      if (!Array.isArray(parsed)) {
        toast.error("JSON must be an array");
        return;
      }
      const items = parsed
        .filter((r: any) => r && typeof r.url === "string" && typeof r.transcripcio === "string")
        .map((r: any) => ({ url: r.url, transcripcio: r.transcripcio }));
      if (items.length === 0) {
        toast.error("No valid entries (need url + transcripcio)");
        return;
      }
      // Small chunks: each entry may trigger an LLM translation, so one big
      // request would exceed the server time limit and import nothing.
      const CHUNK = 2;
      const totals = { updated: 0, notFound: 0, alreadyHad: 0, skippedNoId: 0, failed: 0 };
      let lastError: string | null = null;
      const withTimeout = <T,>(p: Promise<T>, ms: number): Promise<T> =>
        Promise.race([
          p,
          new Promise<T>((_, reject) => setTimeout(() => reject(new Error("Request timed out")), ms)),
        ]);
      for (let i = 0; i < items.length; i += CHUNK) {
        const chunk = items.slice(i, i + CHUNK);
        let ok = false;
        for (let attempt = 0; attempt < 2 && !ok; attempt += 1) {
          try {
            const res: any = await withTimeout(
              importTranscriptsFn({ data: { youtuberId: id, items: chunk } }),
              120_000,
            );
            totals.updated += res.updated ?? 0;
            totals.notFound += res.notFound ?? 0;
            totals.alreadyHad += res.alreadyHad ?? 0;
            totals.skippedNoId += res.skippedNoId ?? 0;
            totals.failed += res.failed ?? 0;
            if (res.lastError) lastError = res.lastError;
            ok = true;
          } catch (e: any) {
            lastError = e?.message ?? String(e);
          }
        }
        if (!ok) totals.failed += chunk.length;
      }
      const summary = `Imported ${totals.updated}/${items.length} · not found: ${totals.notFound} · already had: ${totals.alreadyHad}${totals.skippedNoId ? ` · bad url: ${totals.skippedNoId}` : ""}${totals.failed ? ` · failed: ${totals.failed}` : ""}`;
      if (totals.failed > 0) toast.error(`${summary}${lastError ? ` — ${lastError}` : ""}`);
      else toast.success(summary);
      qc.invalidateQueries({ queryKey: ["admin"] });
    } catch (e: any) {
      toast.error(e.message ?? String(e));
    }
  }

  const pins: MapPinT[] = useMemo(() => {
    return (videos ?? []).flatMap((v: any) =>

      (v.video_locations ?? [])
        .filter((l: any) => typeof l.latitude === "number" && typeof l.longitude === "number")
        .map((l: any) => ({
          id: `${v.id}-${l.id}`,
          lat: l.latitude,
          lng: l.longitude,
          label: cleanTitle(v.title),
          color: "hsl(35 85% 55%)",
        })),
    );
  }, [videos]);

  return (
    <div className="p-10 max-w-7xl">
      {/* Persistent job log */}
      {jobs.length > 0 && (
        <div className="fixed bottom-4 right-4 z-40 w-96 max-w-[calc(100vw-2rem)] space-y-2">
          {jobs.map((j) => {
            const okCount = j.items.filter((i) => i.ok).length;
            const failCount = j.items.filter((i) => !i.ok).length;
            return (
              <div key={j.id} className="bg-paper ring-hairline shadow-lg text-sm">
                <div className="flex items-center justify-between px-3 py-2 border-b border-ink/10 bg-secondary/50">
                  <div className="flex items-center gap-2 min-w-0">
                    {j.running ? (
                      <Loader2 className="size-3.5 animate-spin text-amber flex-shrink-0" />
                    ) : failCount > 0 ? (
                      <AlertCircle className="size-3.5 text-red-600 flex-shrink-0" />
                    ) : (
                      <CheckCircle2 className="size-3.5 text-green-700 flex-shrink-0" />
                    )}
                    <span className="text-mono-caps text-xs truncate">{j.kind}</span>
                    <span className="text-xs text-ink/50">
                      {okCount}✓ {failCount > 0 ? `${failCount}✗` : ""}
                    </span>
                  </div>
                  <button
                    onClick={() => dismissJob(j.id)}
                    className="text-ink/40 hover:text-ink flex-shrink-0"
                    aria-label="Dismiss"
                  >
                    <X className="size-3.5" />
                  </button>
                </div>
                <div className="max-h-72 overflow-y-auto divide-y divide-ink/5">
                  {j.items.length === 0 && j.running && (
                    <div className="px-3 py-2 text-xs text-ink/50">Working…</div>
                  )}
                  {j.items.map((it, idx) => (
                    <div key={idx} className="px-3 py-1.5 flex items-start gap-2">
                      {it.ok ? (
                        <CheckCircle2 className="size-3 text-green-700 flex-shrink-0 mt-0.5" />
                      ) : (
                        <AlertCircle className="size-3 text-red-600 flex-shrink-0 mt-0.5" />
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="text-xs truncate" title={cleanTitle(it.title)}>{cleanTitle(it.title)}</div>
                        {(it.error || it.detail) && (
                          <div className={`text-[11px] ${it.ok ? "text-ink/50" : "text-red-700"}`}>
                            {it.error ?? it.detail}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                {(j.usage.prompt > 0 || j.usage.completion > 0) && (
                  <div className="px-3 py-1.5 border-t border-ink/10 text-[11px] text-ink/60 font-mono flex items-center gap-2">
                    <span>tok: {j.usage.prompt.toLocaleString()} in · {j.usage.completion.toLocaleString()} out</span>
                    <span className="ml-auto text-ink/70">≈ {fmtCost(j.usage.costUsd)}</span>
                  </div>
                )}
                {j.summary && !j.running && (
                  <div className="px-3 py-1.5 border-t border-ink/10 text-xs text-ink/60">{j.summary}</div>
                )}
              </div>
            );
          })}
        </div>
      )}


      <Link
        to="/admin"
        className="inline-flex items-center gap-1.5 text-mono-caps text-ink/50 hover:text-ink mb-4"
      >
        <ArrowLeft className="size-3.5" /> All channels
      </Link>

      {/* Header */}
      <div className="flex items-start gap-6 mb-8">
        <div className="size-20 bg-secondary flex-shrink-0 overflow-hidden">
          {youtuber?.avatar_url && (
            <img src={youtuber.avatar_url} alt="" className="w-full h-full object-cover" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-mono-caps text-amber mb-1">The channel</p>
          {editingName ? (
            <form
              className="flex items-center gap-2"
              onSubmit={(e) => {
                e.preventDefault();
                const n = nameInput.trim();
                if (!n || n === youtuber?.name) { setEditingName(false); return; }
                saveName.mutate(n);
              }}
            >
              <input
                autoFocus
                value={nameInput}
                onChange={(e) => setNameInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Escape") setEditingName(false); }}
                className="text-3xl leading-tight ring-hairline bg-paper px-2 py-1 flex-1 min-w-0 focus:outline-none focus:ring-1 focus:ring-amber"
              />
              <button type="submit" disabled={saveName.isPending} className="bg-ink text-paper px-3 py-2 text-sm hover:bg-amber transition-colors disabled:opacity-50">Save</button>
              <button type="button" onClick={() => setEditingName(false)} className="ring-hairline px-3 py-2 text-sm hover:bg-secondary">Cancel</button>
            </form>
          ) : (
            <h1 className="text-4xl leading-tight flex items-center gap-2 group">
              {youtuber?.name ?? "…"}
              {youtuber && (
                <button
                  onClick={() => { setNameInput(youtuber.name); setEditingName(true); }}
                  className="text-ink/40 hover:text-amber p-1"
                  title="Edit channel name"
                  aria-label="Edit channel name"
                >
                  <Pencil className="size-4" />
                </button>
              )}
            </h1>
          )}
          <div className="text-mono-caps text-ink/50 mt-2">
            @{youtuber?.handle} · {youtuber?.country ?? "—"} ·{" "}
            {formatCompact(youtuber?.subscriber_count ?? 0)} subs
          </div>
          {youtuber && (
            <div className="flex items-center gap-3 mt-3 text-xs">
              <Link
                to="/y/$handle"
                params={{ handle: youtuber.handle }}
                target="_blank"
                className="inline-flex items-center gap-1 ring-hairline px-2.5 py-1 hover:bg-secondary"
              >
                <ExternalLink className="size-3" /> Public page
              </Link>
              <a
                href={`https://www.youtube.com/channel/${youtuber.channel_id}`}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 ring-hairline px-2.5 py-1 hover:bg-secondary"
              >
                <Youtube className="size-3" /> YouTube
              </a>
              <button
                onClick={() => {
                  const next = !(youtuber as any).hidden;
                  if (
                    next &&
                    !confirm(
                      `Hide ${youtuber.name} from the public site? The channel and all its videos will disappear from youtubersmap.com.`,
                    )
                  )
                    return;
                  toggleChannelHidden.mutate(next);
                }}
                disabled={toggleChannelHidden.isPending}
                className={`inline-flex items-center gap-1 ring-hairline px-2.5 py-1 hover:bg-secondary disabled:opacity-50 ${
                  (youtuber as any).hidden ? "bg-amber/15 ring-amber/40 text-amber-deep" : ""
                }`}
                title="Hide or show this channel (and all its videos) on the public site"
              >
                {(youtuber as any).hidden ? (
                  <><Eye className="size-3" /> Show on public site</>
                ) : (
                  <><EyeOff className="size-3" /> Hide from public site</>
                )}
              </button>
            </div>
          )}
          {youtuber && (
            <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
              <label htmlFor="min-import-year" className="text-mono-caps text-ink/50">
                Min import year
              </label>
              <input
                id="min-import-year"
                type="number"
                min={1900}
                max={2200}
                placeholder="—"
                value={minYearInput}
                onChange={(e) => setMinYearInput(e.target.value)}
                className="w-24 bg-paper ring-hairline px-2 py-1"
                title="Videos published before this year are ignored when importing. Leave empty for no limit."
              />
              <button
                onClick={() => {
                  const raw = minYearInput.trim();
                  if (raw === "") return saveMinYear.mutate(null);
                  const n = Number(raw);
                  if (!Number.isInteger(n) || n < 1900 || n > 2200) {
                    toast.error("Enter a valid year (1900–2200) or leave it empty");
                    return;
                  }
                  saveMinYear.mutate(n);
                }}
                disabled={saveMinYear.isPending || !minYearDirty}
                className="ring-hairline px-2.5 py-1 hover:bg-secondary disabled:opacity-40"
              >
                {saveMinYear.isPending ? "Saving…" : "Save"}
              </button>
              <span className="text-ink/40">
                Videos published before this year are skipped on import. Empty = no limit.
              </span>
            </div>
          )}
          {youtuber && (youtuber as any).hidden && (
            <div className="mt-3 text-xs ring-hairline bg-amber/10 ring-amber/30 text-amber-deep px-2.5 py-1.5 inline-flex items-center gap-1.5">
              <EyeOff className="size-3" /> This channel and all its videos are hidden from the public site.
            </div>
          )}
        </div>
        {/* Compact map top-right */}
        <div className="ring-hairline bg-paper flex-shrink-0 w-64">
          <div className="h-40">
            {pins.length > 0 ? (
              <MapView pins={pins} fitToPins initialZoom={1.4} interactive={false} pinSize={6} className="w-full h-full" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-ink/40 text-xs">
                No geolocated videos
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="ring-hairline bg-paper p-5 mb-8 grid gap-5 md:grid-cols-2 md:divide-x md:divide-ink/10">
        {/* Batch actions (limited by the selected count) */}
        <div className="md:pr-5">
          <div className="text-mono-caps text-ink/50 mb-3">Batch actions</div>
          <div className="flex flex-wrap items-center gap-2">
            <select
              value={batchCount}
              onChange={(e) => {
                batchTouched.current = true;
                setBatchCount(Number(e.target.value));
              }}
              className="bg-paper ring-hairline text-sm px-2 py-1.5"
              title="How many videos each batch action processes"
            >
              {BATCH_OPTIONS.map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
            <SplitProgressButton
              onClick={() => runImport("all")}
              progress={importProgress}
              variant="primary"
              icon={<Download className="size-4" />}
              label="Import videos"
              runningLabel="Importing"
              menu={[
                { label: "Import + transcribe + summarize + geolocate", onClick: () => runImport("all") },
                { label: "Import + transcribe + summarize", onClick: () => runImport("summarize") },
                { label: "Import + transcribe", onClick: () => runImport("transcribe") },
                { label: "Import only (metadata)", onClick: () => runImport("import") },
              ]}
            />
            <ProgressButton
              onClick={runDoEverything}
              progress={contentProgress}
              icon={<Zap className="size-4" />}
              label="Process"
              runningLabel="Processing"
              title="For every non-hidden video missing something, generate clean description, summary and/or geolocation as needed."
            />
          </div>
        </div>

        {/* Full-channel actions (ignore the batch count) */}
        <div className="md:pl-5">
          <div className="text-mono-caps text-ink/50 mb-3">Channel-wide actions</div>
          <div className="flex flex-wrap items-center gap-2">
            <ProgressButton
              onClick={runRefreshViews}
              progress={viewsProgress}
              icon={<RefreshCw className="size-4" />}
              label="Refresh views"
              runningLabel="Refreshing"
            />
            <ProgressButton
              onClick={runRefreshMeta}
              progress={metaProgress}
              icon={<RefreshCw className="size-4" />}
              label="Refresh meta"
              runningLabel="Refreshing"
            />
            <button
              onClick={exportMissingTranscriptsCsv}
              className="flex items-center gap-2 px-4 py-2 text-sm ring-hairline hover:bg-secondary transition-colors"
            >
              <FileText className="size-4" />
              Export missing transcripts
            </button>
            <input
              ref={importTranscriptsInputRef}
              type="file"
              accept="application/json,.json"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) handleImportTranscriptsFile(f);
                if (e.target) e.target.value = "";
              }}
            />
            <button
              onClick={() => importTranscriptsInputRef.current?.click()}
              className="flex items-center gap-2 px-4 py-2 text-sm ring-hairline hover:bg-secondary transition-colors"
            >
              <Download className="size-4 rotate-180" />
              Import transcripts
            </button>
          </div>
        </div>
      </div>



      {/* Counts */}
      {(() => {
        const missGeo = counts?.missingGeo ?? 0;
        const missTrans = counts?.missingTranscript ?? 0;
        const missSum = counts?.missingSummary ?? 0;
        const missClean = counts?.missingCleanDescription ?? 0;
        const total = counts?.total ?? (videos?.length ?? 0);
        const badge = (n: number) =>
          `ring-hairline px-2.5 py-1 ${n > 0 ? "bg-amber/10 ring-amber/30 text-amber-deep" : "bg-paper text-ink/50"}`;
        return (
          <div className="flex items-center gap-2 mb-4 text-xs flex-wrap">
            <span className="ring-hairline bg-paper px-2.5 py-1">
              <span className="text-ink/50">Total </span>
              <span className="font-mono font-semibold">{total}</span>
            </span>
            <span className={badge(missGeo)}>
              <MapPin className="size-3 inline mr-1" />
              <span className="font-mono font-semibold">{missGeo}</span> missing geo
            </span>
            <span className={badge(missTrans)}>
              <Sparkles className="size-3 inline mr-1" />
              <span className="font-mono font-semibold">{missTrans}</span> missing transcript
            </span>
            <span className={badge(missSum)}>
              <Sparkles className="size-3 inline mr-1" />
              <span className="font-mono font-semibold">{missSum}</span> missing summary
            </span>
            <span className={badge(missClean)}>
              <Sparkles className="size-3 inline mr-1" />
              <span className="font-mono font-semibold">{missClean}</span> missing clean description
            </span>
          </div>
        );
      })()}


      {/* Filters */}
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <input
          placeholder="Search title…"
          value={filters.search}
          onChange={(e) => setFilters({ ...filters, search: e.target.value })}
          className="ring-hairline bg-paper px-3 py-1.5 text-sm min-w-64"
        />
        <label className="flex items-center gap-2 text-sm">
          <span className="text-ink/50">Sort</span>
          <select
            value={sort}
            onChange={(e) => { setSort(e.target.value as typeof sort); setPage(1); }}
            className="bg-paper ring-hairline px-2 py-1.5 text-sm"
          >
            <option value="newest">Newest first</option>
            <option value="duration_asc">Duration — shortest first</option>
            <option value="duration_desc">Duration — longest first</option>
          </select>
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={filters.onlyMissingGeo}
            onChange={(e) => setFilters({ ...filters, onlyMissingGeo: e.target.checked })}
          />
          Missing geolocation
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={filters.onlyMissingTranscript}
            onChange={(e) => setFilters({ ...filters, onlyMissingTranscript: e.target.checked })}
          />
          Missing transcript
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={filters.onlyMissingSummary}
            onChange={(e) => setFilters({ ...filters, onlyMissingSummary: e.target.checked })}
          />
          Missing summary
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={filters.onlyMissingCleanDescription}
            onChange={(e) => setFilters({ ...filters, onlyMissingCleanDescription: e.target.checked })}
          />
          Missing clean description
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={filters.includeHidden}
            onChange={(e) => setFilters({ ...filters, includeHidden: e.target.checked })}
          />
          Include hidden
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={filters.includeDeleted}
            onChange={(e) => setFilters({ ...filters, includeDeleted: e.target.checked })}
          />
          Include deleted
        </label>
      </div>



      {(() => {
        const all = videos ?? [];
        // Videos with no duration always go last, whichever direction is picked.
        const sorted =
          sort === "newest"
            ? all
            : [...all].sort((a: any, b: any) => {
                const da = a.duration_seconds ?? null;
                const db = b.duration_seconds ?? null;
                if (da == null && db == null) return 0;
                if (da == null) return 1;
                if (db == null) return -1;
                return sort === "duration_asc" ? da - db : db - da;
              });
        const totalPages = Math.max(1, Math.ceil(sorted.length / perPage));
        const curPage = Math.min(page, totalPages);
        const start = (curPage - 1) * perPage;
        const slice = sorted.slice(start, start + perPage);
        const Pager = () => (
          <div className="flex items-center gap-2 text-xs text-ink/60 py-2">
            <span>Per page</span>
            <select
              value={perPage}
              onChange={(e) => { setPerPage(Number(e.target.value)); setPage(1); }}
              className="bg-paper ring-hairline px-2 py-1"
            >
              {PAGE_SIZE_OPTIONS.map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
            {all.length > 0 && (
              <span className="ml-2">
                {start + 1}–{Math.min(start + perPage, all.length)} of {all.length}
              </span>
            )}
            <div className="ml-auto flex items-center gap-1">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={curPage <= 1}
                className="ring-hairline px-2 py-1 hover:bg-secondary disabled:opacity-40"
              >
                Prev
              </button>
              <span className="px-2">Page {curPage} / {totalPages}</span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={curPage >= totalPages}
                className="ring-hairline px-2 py-1 hover:bg-secondary disabled:opacity-40"
              >
                Next
              </button>
            </div>
          </div>
        );
        return isLoading ? (
          <p className="text-ink/40">Loading…</p>
        ) : all.length === 0 ? (
          <p className="text-ink/50 text-center py-12">No videos yet — click "Import videos".</p>
        ) : (
          <>
            <Pager />
            <div className="space-y-1.5">
              {slice.map((v: any) => (
                <VideoRow
                  key={v.id}
                  v={v}
                  onUpdate={(patch) => upd.mutate({ id: v.id, patch })}
                  logger={{ startJob, appendJobItems, addJobUsage, finishJob }}
                />
              ))}
            </div>

            <Pager />
          </>
        );
      })()}
    </div>
  );
}

function StatusDot({ state, label }: { state: "ok" | "missing" | "warn"; label: string }) {
  const cls =
    state === "ok"
      ? "bg-emerald-500"
      : state === "warn"
      ? "bg-amber-500"
      : "bg-red-500/80";
  const suffix = state === "ok" ? "OK" : state === "warn" ? "partial" : "missing";
  return (
    <span
      title={`${label}: ${suffix}`}
      className={`inline-block size-3 ring-1 ring-ink/20 ${cls}`}
    />
  );
}

function SplitProgressButton({
  onClick,
  progress,
  icon,
  label,
  runningLabel,
  menu,
  variant = "outline",
}: {
  onClick: () => void;
  progress: Progress;
  icon: React.ReactNode;
  label: string;
  runningLabel?: string;
  menu: { label: string; onClick: () => void }[];
  variant?: "primary" | "outline";
}) {
  const [open, setOpen] = useState(false);
  const running = progress !== null;
  const pct = progress && !progress.indeterminate && progress.total > 0
    ? Math.min(100, (progress.current / progress.total) * 100)
    : 0;
  const mainCls =
    variant === "primary"
      ? "relative overflow-hidden flex items-center gap-2 px-4 py-2 text-sm bg-ink text-paper hover:bg-amber transition-colors disabled:opacity-70"
      : "relative overflow-hidden flex items-center gap-2 px-4 py-2 text-sm ring-hairline hover:bg-secondary transition-colors disabled:opacity-70";
  const chevCls =
    variant === "primary"
      ? "bg-ink text-paper border-l border-paper/20 px-1.5 hover:bg-amber disabled:opacity-70"
      : "ring-hairline border-l-0 px-1.5 hover:bg-secondary disabled:opacity-70";
  return (
    <div className="relative inline-flex">
      <button
        onClick={onClick}
        disabled={running}
        className={mainCls}
      >
        {running && (
          <span
            aria-hidden
            className="absolute inset-y-0 left-0 bg-amber/30 transition-all duration-200"
            style={{ width: `${pct}%` }}
          />
        )}
        <span className="relative flex items-center gap-2">
          {icon}
          {running ? `${runningLabel ?? label} ${progress?.current}/${progress?.total}` : label}
        </span>
      </button>
      <button
        onClick={() => setOpen((s) => !s)}
        disabled={running}
        className={chevCls}
        title="More options"
      >
        <ChevronDown className="size-3.5" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute top-full right-0 mt-1 z-20 bg-paper ring-hairline shadow-lg min-w-64">
            {menu.map((m, i) => (
              <button
                key={i}
                onClick={() => { setOpen(false); m.onClick(); }}
                className="block w-full text-left px-3 py-2 text-sm hover:bg-secondary"
              >
                {m.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function RowActionButton({
  onClick,
  running,
  title,
  icon,
}: {
  onClick: (e: React.MouseEvent) => void;
  running: boolean;
  title: string;
  icon: React.ReactNode;
}) {
  return (
    <button
      onClick={(e) => {
        e.stopPropagation();
        e.preventDefault();
        onClick(e);
      }}
      disabled={running}
      title={title}
      className="ring-hairline text-xs w-7 h-7 grid place-items-center hover:bg-secondary transition-colors flex-shrink-0 disabled:opacity-50"
    >
      {running ? <Loader2 className="size-3 animate-spin" /> : icon}
    </button>
  );
}

function VideoRow({
  v,
  onUpdate,
  logger,
}: {
  v: any;
  onUpdate: (patch: any) => void;
  logger: {
    startJob: (kind: string) => number;
    appendJobItems: (id: number, items: { title: string; ok: boolean; error: string | null; detail?: string }[]) => void;
    addJobUsage: (id: number, u: any) => void;
    finishJob: (id: number, summary: string) => void;
  };
}) {
  const { i18n } = useTranslation();
  const qc = useQueryClient();
  const hasGeo = v.geolocated && (v.video_locations?.length ?? 0) > 0;
  const hasDesc = !!(v.description && String(v.description).trim());
  const hasDescClean = !!(v.description_clean && String(v.description_clean).trim());
  const hasTranscript = !!(v.transcript && String(v.transcript).trim());
  const hasSummary = !!(v.transcript_summary && String(v.transcript_summary).trim());
  const missingGeo = !hasGeo;
  const missingContent = !hasSummary || !hasDescClean || !hasTranscript;
  const flagged = missingGeo || missingContent;

  const geoFn = useServerFn(geolocateVideoOne);
  const contentFn = useServerFn(processVideoContent);
  const transcribeFn = useServerFn(retranscribeVideo);
  const [busy, setBusy] = useState<null | "geo" | "content" | "transcribe" | "all">(null);

  async function runGeo() {
    if (busy) return;
    setBusy("geo");
    const jobId = logger.startJob("Geolocate (row)");
    try {
      const r: any = await geoFn({ data: { id: v.id, replace: hasGeo } });
      logger.addJobUsage(jobId, r?.usage);
      const ok = (r.inserted ?? 0) > 0;
      logger.appendJobItems(jobId, [{
        title: v.title,
        ok,
        error: ok ? null : r.error ?? "No locations returned",
        detail: ok ? `${r.inserted} location${r.inserted === 1 ? "" : "s"}` : undefined,
      }]);
      logger.finishJob(jobId, ok ? `Geolocated · ${r.inserted}` : "No locations");
      qc.invalidateQueries({ queryKey: ["admin"] });
    } catch (e: any) {
      logger.appendJobItems(jobId, [{ title: v.title, ok: false, error: e?.message ?? String(e) }]);
      logger.finishJob(jobId, `Error: ${e.message}`);
    } finally {
      setBusy(null);
    }
  }
  async function runContent() {
    if (busy) return;
    setBusy("content");
    const jobId = logger.startJob("Process content (row)");
    try {
      const r: any = await contentFn({ data: { id: v.id } });
      if (Array.isArray(r?.usages)) r.usages.forEach((u: any) => logger.addJobUsage(jobId, u));
      else logger.addJobUsage(jobId, r?.usage);
      const ok = r?.ok !== false;
      logger.appendJobItems(jobId, [{ title: v.title, ok, error: ok ? null : r?.error ?? "Failed" }]);
      logger.finishJob(jobId, ok ? "Processed" : "Failed");
      qc.invalidateQueries({ queryKey: ["admin"] });
    } catch (e: any) {
      logger.appendJobItems(jobId, [{ title: v.title, ok: false, error: e?.message ?? String(e) }]);
      logger.finishJob(jobId, `Error: ${e.message}`);
    } finally {
      setBusy(null);
    }
  }
  async function runTranscribe() {
    if (busy) return;
    setBusy("transcribe");
    const jobId = logger.startJob("Transcribe (row)");
    try {
      const r: any = await transcribeFn({ data: { id: v.id, method: "auto" } });
      logger.addJobUsage(jobId, r?.usage);
      const ok = !!r?.transcript;
      logger.appendJobItems(jobId, [{
        title: v.title,
        ok,
        error: ok ? null : r?.error ?? "Transcription failed",
        detail: ok ? `via ${r.source}` : undefined,
      }]);
      logger.finishJob(jobId, ok ? `Transcribed via ${r.source}` : "Failed");
      qc.invalidateQueries({ queryKey: ["admin"] });
    } catch (e: any) {
      logger.appendJobItems(jobId, [{ title: v.title, ok: false, error: e?.message ?? String(e) }]);
      logger.finishJob(jobId, `Error: ${e.message}`);
    } finally {
      setBusy(null);
    }
  }
  // Do everything: transcribe → summarize → geolocate.
  async function runAll() {
    if (busy) return;
    setBusy("all");
    const jobId = logger.startJob("Do all (row)");
    try {
      if (!hasTranscript) {
        const r: any = await transcribeFn({ data: { id: v.id, method: "auto" } });
        logger.addJobUsage(jobId, r?.usage);
        if (!r.transcript) {
          logger.appendJobItems(jobId, [{ title: v.title, ok: false, error: r.error ?? "Transcription failed" }]);
          logger.finishJob(jobId, "Failed at transcribe");
          return;
        }
      }
      const pr: any = await contentFn({ data: { id: v.id, mode: "summarize" } });
      logger.addJobUsage(jobId, pr?.usage);
      const gr: any = await geoFn({ data: { id: v.id, replace: hasGeo } });
      logger.addJobUsage(jobId, gr?.usage);
      const inserted = gr?.inserted ?? 0;
      logger.appendJobItems(jobId, [{
        title: v.title,
        ok: true,
        error: null,
        detail: `${inserted} location${inserted === 1 ? "" : "s"}`,
      }]);
      logger.finishJob(jobId, inserted > 0 ? `Done · ${inserted}` : "Done · no locations");
      qc.invalidateQueries({ queryKey: ["admin"] });
    } catch (e: any) {
      logger.appendJobItems(jobId, [{ title: v.title, ok: false, error: e?.message ?? String(e) }]);
      logger.finishJob(jobId, `Error: ${e.message}`);
    } finally {
      setBusy(null);
    }
  }


  return (
    <div
      className={`ring-hairline ${
        v.hidden ? "bg-destructive/5" : flagged ? "bg-amber/10 ring-amber/30" : "bg-paper"
      } hover:bg-secondary/50 transition-colors`}
    >
      <div className="px-3 py-2 flex items-center gap-3">
        <Link
          to="/admin/videos/$id"
          params={{ id: v.id }}
          className="flex items-center gap-3 flex-1 min-w-0"
        >
          <div className="w-20 aspect-video bg-secondary flex-shrink-0 overflow-hidden">
            {v.thumbnail_url && <img src={v.thumbnail_url} alt="" className="w-full h-full object-cover" />}
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-serif text-sm leading-tight line-clamp-1 hover:text-amber">
              {cleanTitle(v.title)}
            </div>
            <div className="flex items-center gap-2 text-mono-caps text-ink/40 mt-0.5 text-[10px] flex-wrap">
              <span>{formatDate(v.published_at, i18n.language)}</span>
              <span>·</span>
              <span>{formatCompact(v.view_count)} views</span>
              <span>·</span>
              <span>{formatDuration(v.duration_seconds)}</span>
              {hasGeo && (
                <>
                  <span>·</span>
                  <span className="text-amber inline-flex items-center gap-1">
                    <MapPin className="size-3" /> {v.video_locations.length}
                  </span>
                </>
              )}
              {v.hidden && <><span>·</span><span className="text-ink/60">HIDDEN</span></>}
              {v.deleted && <><span>·</span><span className="text-destructive">DELETED ON YOUTUBE</span></>}
            </div>
          </div>
        </Link>
        <div className="flex items-center gap-1 flex-shrink-0">
          <StatusDot
            state={!hasGeo ? "missing" : v.geo_source === "description" ? "warn" : "ok"}
            label={
              !hasGeo
                ? "Geolocation"
                : v.geo_source === "manual"
                ? "Geolocation (manual)"
                : v.geo_source === "description"
                ? "Geolocation (from description only)"
                : "Geolocation (from transcript)"
            }
          />
          <StatusDot
            state={!hasDesc ? "missing" : !hasDescClean ? "warn" : "ok"}
            label={!hasDesc ? "Description missing" : !hasDescClean ? "Clean description missing" : "Description"}
          />
          <StatusDot
            state={!hasTranscript ? "missing" : !hasSummary ? "warn" : "ok"}
            label={!hasTranscript ? "Transcript missing" : !hasSummary ? "Summary missing" : "Transcript"}
          />

        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <RowActionButton
            onClick={runTranscribe}
            running={busy === "transcribe"}
            title="Transcribe"
            icon={<FileText className="size-3.5" />}
          />
          <RowActionButton
            onClick={runContent}
            running={busy === "content"}
            title="Process content (clean + summary)"
            icon={<Sparkles className="size-3.5" />}
          />
          <RowActionButton
            onClick={runGeo}
            running={busy === "geo"}
            title={hasGeo ? "Re-geolocate (replaces locations)" : "Geolocate"}
            icon={<MapPin className="size-3.5" />}
          />
          <RowActionButton
            onClick={runAll}
            running={busy === "all"}
            title="Do everything: transcribe → summarize → geolocate"
            icon={<Zap className="size-3.5" />}
          />
          <button
            onClick={(e) => { e.stopPropagation(); onUpdate({ hidden: !v.hidden }); }}
            className="ring-hairline text-xs px-2 py-1.5 hover:bg-secondary transition-colors flex items-center flex-shrink-0"
            title={v.hidden ? "Unhide" : "Hide"}
            aria-label={v.hidden ? "Unhide" : "Hide"}
          >
            {v.hidden ? <Eye className="size-3.5" /> : <EyeOff className="size-3.5" />}
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onUpdate({ deleted: !v.deleted }); }}
            className={`ring-hairline text-xs px-2.5 py-1 transition-colors flex items-center gap-1 flex-shrink-0 ${v.deleted ? "text-destructive bg-destructive/10 hover:bg-destructive/20" : "hover:bg-secondary"}`}
            title={v.deleted ? "Video is marked as deleted/private on YouTube — click to restore" : "Mark as deleted/private on YouTube (hidden from the public site)"}
          >
            {v.deleted ? <><RotateCcw className="size-3" /> Restore</> : <><Ban className="size-3" /> Deleted</>}
          </button>
        </div>
      </div>
    </div>
  );
}

